import { useEffect, useState } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useListingStatusContext } from 'src/contexts/ListingStatusContext';
import { SimpleTable } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  NonEventMappingMarketplaces,
  PosManagedMarketplace,
} from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { compareMarketplace } from 'src/utils/eventWithDataUtils';
import { shouldShowExternalMarketplaceBroadcastForEvent } from 'src/utils/marketplaceUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ListingDetails,
  ListingMetrics,
  ListingStatus,
  Marketplace,
  MarketplaceListing,
} from 'src/WebApiController';

import { useMarketplaceBroadcastSection } from './MarketplaceBroadcastSection.hooks';
import {
  hasPendingMarketplaces,
  updateMarketplaceStatusesAndListing,
} from './MarketplaceBroadcastSection.utils';
import { MarketplaceDisplay } from './MarketplaceDisplay';
import * as styles from './MarketplaceDisplay.css';

// Hoisted to module level to avoid re-creating the Set on every render.
const KNOWN_MARKETPLACE_VALUES = new Set<string>(Object.values(Marketplace));

const getAnyTicketsViagogoInternational = (
  listings: MarketplaceListing[] | null
): MarketplaceListing | undefined =>
  listings?.find((mk) => mk.mkp === Marketplace.ViagogoInternational);

export const MarketplaceTable = ({
  listing,
  disabled,
  marketplaceListingStatus,
  setMarketplaceListingStatus,
}: {
  listing: ListingDetails;
  disabled?: boolean;
  marketplaceListingStatus: Record<Marketplace, ListingStatus>;
  setMarketplaceListingStatus: React.Dispatch<
    React.SetStateAction<Record<Marketplace, ListingStatus>>
  >;
}) => {
  const hasShowGigsbergFeature = useUserHasFeature(
    Feature.ShowGigsbergInBroadcastTableForIntlEvents
  );
  const hasHideUnbroadcastableMarketplaces = useUserHasFeature(
    Feature.HideUnbroadcastableMarketplaces
  );
  const { setActivePosEntity, updateActivePosEntityInfo } =
    useActivePosEntityContext<ListingDetails>();

  const { refreshMetrics } = useCatalogMetricsContext<ListingMetrics>();

  const [isLoading, setIsLoading] = useState(false);

  const { trackError } = useClientLogContext();

  const { mkpListings } = listing;

  const { getMarketplaceDisplayProps, event, venue, isInternationalEvent } =
    useMarketplaceBroadcastSection(
      listing,
      disabled ?? false,
      setIsLoading,
      marketplaceListingStatus,
      setMarketplaceListingStatus
    );

  const { getOrSubscribeForListingStatus, isLoading: statusIsLoading } =
    useListingStatusContext();

  useEffect(() => {
    const pollingFrequency = 2_000;
    // If we have any Marketplace listing pending status - we need to poll to get the latest status
    const statusChecking = setInterval(async () => {
      const shouldCheckStatus =
        hasPendingMarketplaces(marketplaceListingStatus) &&
        !isLoading &&
        !statusIsLoading;

      if (shouldCheckStatus) {
        await tryInvokeApi(
          async () => {
            setIsLoading(true);

            const newListing = getOrSubscribeForListingStatus(listing.id);

            if (newListing) {
              updateMarketplaceStatusesAndListing(
                statusChecking,
                listing,
                newListing,
                marketplaceListingStatus,
                setMarketplaceListingStatus,
                (updatedListing) => {
                  refreshMetrics?.();
                  updateActivePosEntityInfo({
                    event: event,
                    posEntity: updatedListing,
                    posEntityId: newListing.id,
                    posEntityDisplayId: newListing.idOnMkp,
                  });
                }
              );
            }
          },
          (error) => {
            // Because this is a background process - we don't want to show error dialog
            trackError('ListingClient.getListingStatusByListingId', error, {
              listingId: listing.id,
            });
          },
          () => setIsLoading(false)
        );
      }
    }, pollingFrequency);

    return () => clearInterval(statusChecking);
  }, [
    event,
    listing,
    listing.id,
    listing.idOnMkp,
    marketplaceListingStatus,
    mkpListings,
    refreshMetrics,
    setActivePosEntity,
    updateActivePosEntityInfo,
    trackError,
    isLoading,
    setMarketplaceListingStatus,
    getOrSubscribeForListingStatus,
    statusIsLoading,
  ]);

  const anyticketViagogoInterational =
    getAnyTicketsViagogoInternational(mkpListings);

  return (
    <SimpleTable.Table>
      <SimpleTable.Thead>
        <SimpleTable.Tr>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Merchant} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Broadcast} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Status} />
          </SimpleTable.Th>

          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Section} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Row} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Seats} />
          </SimpleTable.Th>

          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.ListingId} />
          </SimpleTable.Th>
        </SimpleTable.Tr>
      </SimpleTable.Thead>
      <SimpleTable.Tbody>
        {mkpListings
          ?.filter(
            (ml) =>
              ml.mkp &&
              // Guard against unknown numeric marketplace values (e.g. raw `22`
              // when the Utilities NuGet package predates SellBack = 22).
              KNOWN_MARKETPLACE_VALUES.has(String(ml.mkp)) &&
              !PosManagedMarketplace.includes(ml.mkp) &&
              (!hasHideUnbroadcastableMarketplaces ||
                !NonEventMappingMarketplaces.includes(ml.mkp))
          )
          .toSorted((a, b) => compareMarketplace(a.mkp, b.mkp))
          .flatMap((ml, i) => {
            if (ml.mkp === Marketplace.StubHub) {
              return [
                <MarketplaceDisplay
                  {...getMarketplaceDisplayProps(ml, false, i)}
                  key={`${ml.mkpListingId}-${ml.mkp}-${false}`}
                />,
                <MarketplaceDisplay
                  {...getMarketplaceDisplayProps(
                    anyticketViagogoInterational ?? ml,
                    true,
                    i
                  )}
                  key={`${ml.mkpListingId}-${ml.mkp}-${true}`}
                />,
              ];
            }

            const shouldShowExternalMarketplace = hasShowGigsbergFeature
              ? shouldShowExternalMarketplaceBroadcastForEvent(
                  ml.mkp!,
                  venue?.ctryCode
                )
              : !isInternationalEvent;
            return shouldShowExternalMarketplace
              ? [
                  <MarketplaceDisplay
                    {...getMarketplaceDisplayProps(ml, false, i)}
                    key={`${ml.mkpListingId}-${ml.mkp}-${false}`}
                  />,
                ]
              : [];
          })}
      </SimpleTable.Tbody>
    </SimpleTable.Table>
  );
};
